import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Drawer, Modal, Input, message } from 'antd';
import { useHistory } from 'react-router-dom';
import '../../styles/mobile.css';
import { trackAddToCart, trackViewCart, trackBeginCheckout, trackPurchase, trackProductInteraction, trackBlogView, trackNewsletterSignup } from '../../utils/analytics';
import { getFeaturedPosts } from '../../data/blogPosts';
import SEOHead from '../../components/SEOHead';
import FAQSection, { homepageFAQs } from '../../components/FAQSection';
import { generateOrganizationSchema, generateWebsiteSchema, generateProductSchema } from '../../utils/schemaMarkup';
import { 
  ShoppingCartOutlined,
  PlusOutlined,
  MinusOutlined,
  MenuOutlined
} from '@ant-design/icons';
import StripeCheckout from '../components/checkout/StripeCheckout';
import calmiImage from '../../assets/images/Calmi.png';
import classicBearImage from '../../assets/images/Classic Bear.png';
import unicornImage from '../../assets/images/Classic Unicorn.png';
import lionImage from '../../assets/images/Classic Lion.png';
import squishyBearImage from '../../assets/images/Squish Bear.png';
import squishyUnicornImage from '../../assets/images/Squish Unicorn.png';
import squishyLionImage from '../../assets/images/Squish Lion.png';

const { Title, Text, Paragraph } = Typography;

const Calmi = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [cartVisible, setCartVisible] = useState(false);
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [selectedProduct] = useState('lumi-classic-bear');
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Lumi Bears",
      "description": "Premium comfort companions designed to bring peace and emotional support to children and families.",
      "url": "https://lumibears.com",
      "logo": "https://lumibears.com/logo.png",
      "sameAs": [],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "support@lumibears.com"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://lumibears.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Lumi Bears Product Catalog",
        "itemListElement": Object.values(products).map(product => ({
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": `https://lumibears.com${product.image}`,
          "offers": {
            "@type": "Offer",
            "price": product.price,
            "priceCurrency": "USD",
            "availability": "https://schema.org/PreOrder",
            "url": `https://lumibears.com/products/${product.id}`
          },
          "category": product.collection,
          "brand": {
            "@type": "Brand",
            "name": "Lumi Bears"
          }
        }))
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('Lumi Bears')) {
        existingScript.remove();
      }
    };
  }, []);

  // Product catalog
  const products = {
    // Classic Bears Collection
    'lumi-classic-bear': {
      id: 'lumi-classic-bear',
      name: 'Lumi Classic Bear',
      price: 499,
      image: classicBearImage,
      collection: 'Classic Bears',
      subtitle: 'The original cozy companion',
      description: 'Perfect for children who need steady emotional support. The warm honey brown plush and gentle expression provide comfort during overwhelming moments, bedtime worries, or when parents are busy with work or other children.',
      specs: { dimensions: '12" x 8" x 6"', color: 'Honey Brown', texture: 'Classic Plush' }
    },
    'lumi-unicorn': {
      id: 'lumi-unicorn',
      name: 'Lumi Unicorn',
      price: 499,
      image: unicornImage,
      collection: 'Classic Bears',
      subtitle: 'Mystical imagination companion',
      description: 'Ideal for children with vivid imaginations who sometimes feel misunderstood. The mystical unicorn design appeals to kids who love fantasy while providing real emotional support during social challenges or big transitions.',
      specs: { dimensions: '12" x 8" x 6"', color: 'Cream White', texture: 'Magical Plush' }
    },
    'lumi-lion': {
      id: 'lumi-lion',
      name: 'Lumi Lion',
      price: 499,
      image: lionImage,
      collection: 'Classic Bears',
      subtitle: 'Brave and noble companion',
      description: 'Great for children who need extra confidence in social situations. The brave lion expression helps shy or anxious kids find their inner strength when facing new schools, making friends, or dealing with family changes.',
      specs: { dimensions: '12" x 8" x 6"', color: 'Golden Orange', texture: 'Maned Plush' }
    },
    
    // Squishy Collection
    'lumi-squishy-bear': {
      id: 'lumi-squishy-bear',
      name: 'Lumi Squishy Bear',
      price: 499,
      image: squishyBearImage,
      collection: 'Squishy Collection',
      subtitle: 'Ultra-huggable round companion',
      description: 'Specially designed for children who need extra sensory comfort. The ultra-squishy design provides calming tactile feedback for kids who feel overwhelmed, struggle with big emotions, or need extra support during stressful times.',
      specs: { dimensions: '10" x 10" x 8"', color: 'Honey Brown', texture: 'Ultra-Squishy Plush' }
    },
    'lumi-squishy-unicorn': {
      id: 'lumi-squishy-unicorn',
      name: 'Lumi Squishy Unicorn',
      price: 499,
      image: squishyUnicornImage,
      collection: 'Squishy Collection',
      subtitle: 'Magical round dreamer',
      description: 'Perfect for sensitive children who experience the world intensely. The soft, squishy texture combined with magical design provides comfort for kids with social anxiety, sensory needs, or who feel different from their peers.',
      specs: { dimensions: '10" x 10" x 8"', color: 'Cream Pink', texture: 'Magic-Squishy Plush' }
    },
    'lumi-squishy-lion': {
      id: 'lumi-squishy-lion',
      name: 'Lumi Squishy Lion',
      price: 499,
      image: squishyLionImage,
      collection: 'Squishy Collection',
      subtitle: 'Courageous round friend',
      description: 'Designed for children facing big challenges who need both courage and comfort. The brave lion design inspires confidence while the squishy texture provides calming sensory input for kids dealing with family stress or major life changes.',
      specs: { dimensions: '10" x 10" x 8"', color: 'Golden', texture: 'Brave-Squishy Plush' }
    }
  };

  const currentProduct = products[selectedProduct];
  const collections = ['Classic Bears', 'Squishy Collection'];

  // Product route mapping
  const getProductRoute = (productId) => {
    const routeMap = {
      'lumi-classic-bear': '/products/lumi-classic-bear',
      'lumi-unicorn': '/products/lumi-unicorn',
      'lumi-lion': '/products/lumi-lion',
      'lumi-squishy-bear': '/products/lumi-squishy-bear',
      'lumi-squishy-unicorn': '/products/lumi-squishy-unicorn',
      'lumi-squishy-lion': '/products/lumi-squishy-lion'
    };
    return routeMap[productId] || '/';
  };

  const addToCart = (productId, quantity = 1) => {
    const product = products[productId] || currentProduct;
    const existingItem = cartItems.find(item => item.id === product.id);
    
    // Track add to cart event
    trackAddToCart(product, quantity);
    
    if (existingItem) {
      setCartItems(cartItems.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCartItems([...cartItems, { ...product, quantity }]);
    }
    setCartVisible(true);
  };

  const updateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      setCartItems(cartItems.filter(item => item.id !== itemId));
    } else {
      setCartItems(cartItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    // Track purchase completion
    const orderData = {
      transactionId: paymentIntent.id,
      total: getTotalPrice(),
      items: cartItems
    };
    trackPurchase(orderData);
    
    setCheckoutVisible(false);
    setCartVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your Lumi preorder has been confirmed! Your Lumi is guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  const handleNewsletterSignup = async () => {
    if (!email || !email.includes('@')) {
      message.error('Please enter a valid email address');
      return;
    }

    setIsSubscribing(true);
    
    // Simulate API call (replace with actual newsletter service integration)
    try {
      // Track the newsletter signup event
      trackNewsletterSignup('homepage');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Thank you for subscribing! Check your email for our latest insights on children\'s emotional wellness.');
      setEmail('');
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Generate structured data for homepage
  const homepageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(),
      generateWebsiteSchema(),
      {
        "@type": "WebPage",
        "name": "Lumi Bears - Wellness Companions for Children",
        "description": "Premium comfort companions designed to bring peace and emotional support to children and families through innovative wellness technology.",
        "url": "https://lumibears.com",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Lumi Bears",
          "url": "https://lumibears.com"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": "https://lumibears.com/api/placeholder/1200/630",
          "width": 1200,
          "height": 630
        }
      }
    ]
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      <SEOHead
        title="Lumi Bears - Wellness Companions for Children"
        description="Premium comfort companions designed to bring peace and emotional support to children and families. Innovative emotional wellness technology with screen-free interaction, perfect for anxiety relief, bedtime routines, and emotional regulation."
        keywords="emotional support stuffed animals, wellness companions, children comfort toys, anxiety support toys, bedtime routine helpers, emotional regulation tools, screen-free technology, AI interactive plush, sensory comfort bears, mindfulness toys for kids"
        canonicalUrl="/"
        ogType="website"
        structuredData={homepageSchema}
      />
      {/* Header Navigation */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '15px 0',
        marginBottom: '0'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px'
        }}>
          {/* Mobile Layout */}
          <div style={{ display: 'block' }}>
            {/* Top row with logo and menu button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <img 
                  src={calmiImage} 
                  alt="Lumi Bears"
                  style={{ height: '32px', marginRight: '12px', flexShrink: 0 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML += '<span style="color: white; font-size: 20px; font-weight: bold; margin-right: 12px;">🧸</span>';
                  }}
                />
                <Title level={4} style={{ 
                  color: 'white', 
                  margin: 0, 
                  fontSize: '18px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  Lumi Bears
                </Title>
              </div>
              
              {/* Desktop navigation - hidden on mobile */}
              <div className="desktop-nav" style={{ 
                gap: '8px', 
                alignItems: 'center'
              }}>
                <Button
                  onClick={() => history.push('/wellness')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '20px',
                    height: '36px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '0 16px'
                  }}
                >
                  How We Help
                </Button>
                <Button
                  onClick={() => history.push('/platform')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '20px',
                    height: '36px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '0 16px'
                  }}
                >
                  Safety
                </Button>
                <Button
                  onClick={() => history.push('/blog')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '20px',
                    height: '36px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '0 16px'
                  }}
                >
                  Blog
                </Button>
                <Badge count={getTotalItems()} size="small">
                  <Button
                    type="default"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => setCartVisible(true)}
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white', 
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '20px',
                      height: '36px',
                      fontWeight: '500',
                      padding: '0 16px'
                    }}
                  >
                    Cart
                  </Button>
                </Badge>
              </div>

              {/* Mobile menu button and cart */}
              <div className="mobile-nav" style={{ gap: '8px', alignItems: 'center' }}>
                <Badge count={getTotalItems()} size="small">
                  <Button
                    type="default"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => {
                      trackViewCart(cartItems);
                      setCartVisible(true);
                    }}
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white', 
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '20px',
                      height: '36px',
                      fontWeight: '500',
                      padding: '0 12px',
                      minWidth: 'auto'
                    }}
                  />
                </Badge>
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setMobileMenuVisible(true)}
                  style={{
                    color: 'white',
                    fontSize: '18px',
                    padding: '0',
                    height: '36px',
                    width: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Button
            type="text"
            onClick={() => {
              history.push('/wellness');
              setMobileMenuVisible(false);
            }}
            style={{
              textAlign: 'left',
              fontSize: '16px',
              height: '48px',
              justifyContent: 'flex-start'
            }}
          >
            🌱 How We Help
          </Button>
          <Button
            type="text"
            onClick={() => {
              history.push('/platform');
              setMobileMenuVisible(false);
            }}
            style={{
              textAlign: 'left',
              fontSize: '16px',
              height: '48px',
              justifyContent: 'flex-start'
            }}
          >
            🛡️ Safety
          </Button>
          <Button
            type="text"
            onClick={() => {
              history.push('/blog');
              setMobileMenuVisible(false);
            }}
            style={{
              textAlign: 'left',
              fontSize: '16px',
              height: '48px',
              justifyContent: 'flex-start'
            }}
          >
            📚 Blog
          </Button>
        </div>
      </Drawer>

      {/* Content */}
      <div style={{ paddingTop: '40px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
          {/* Hero Section */}
          <div className="hero-section" style={{ 
            marginBottom: '80px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '20px',
            padding: '60px 40px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Row gutter={[48, 32]} align="middle" style={{ position: 'relative', zIndex: 2 }}>
              <Col xs={24} lg={12}>
                <Title level={1} className="hero-title" style={{ 
                  fontSize: '3.5rem', 
                  marginBottom: '20px', 
                  color: '#2c3e50',
                  fontWeight: '700',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}>
                  When Your Child Needs Someone Who Always Understands
                </Title>
                <Paragraph className="hero-paragraph" style={{ 
                  fontSize: '22px', 
                  color: '#7f8c8d', 
                  marginBottom: '40px',
                  textAlign: 'center'
                }}>
                  Every child faces moments when they need extra support, comfort, or simply 
                  someone who listens without judgment. Lumi companions are here for those 
                  moments—offering gentle guidance and endless patience.
                </Paragraph>
              </Col>
              <Col xs={24} lg={12} style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: '20px',
                  padding: '40px',
                  display: 'inline-block',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}>
                  <img 
                    src={classicBearImage} 
                    alt="Lumi Wellness Companion"
                    style={{ 
                      maxWidth: '300px',
                      width: '100%',
                      height: 'auto',
                      borderRadius: '15px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<div style="font-size: 150px; opacity: 0.8;">🧸</div>';
                    }}
                  />
                </div>
              </Col>
            </Row>
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <Row gutter={[32, 16]} justify="center" style={{ marginBottom: '40px' }}>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <div className="feature-icon" style={{ fontSize: '48px', marginBottom: '8px' }}>🤗</div>
                    <Text strong>Always Available</Text><br/>
                    <Text type="secondary">When You Can't Be</Text>
                  </div>
                </Col>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <div className="feature-icon" style={{ fontSize: '48px', marginBottom: '8px' }}>💙</div>
                    <Text strong>Gentle Support</Text><br/>
                    <Text type="secondary">For Big Emotions</Text>
                  </div>
                </Col>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <div className="feature-icon" style={{ fontSize: '48px', marginBottom: '8px' }}>🎙️</div>
                    <Text strong>Voice Only</Text><br/>
                    <Text type="secondary">No Screens</Text>
                  </div>
                </Col>
                <Col>
                  <div style={{ textAlign: 'center' }}>
                    <div className="feature-icon" style={{ fontSize: '48px', marginBottom: '8px' }}>👨‍👩‍👧‍👦</div>
                    <Text strong>Parent Dashboard</Text><br/>
                    <Text type="secondary">Complete Oversight</Text>
                  </div>
                </Col>
              </Row>
              <Button 
                type="primary" 
                size="large"
                style={{
                  background: '#e74c3c',
                  borderColor: '#e74c3c',
                  height: '50px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '0 40px',
                  marginRight: '16px'
                }}
                onClick={() => {
                  document.getElementById('product-collections').scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                }}
              >
                Find Your Child's Companion
              </Button>
              <Button 
                size="large"
                style={{
                  height: '50px',
                  fontSize: '18px',
                  padding: '0 40px'
                }}
                onClick={() => history.push('/wellness')}
              >
                See How Lumi Helps
              </Button>
            </div>
          </div>

          {/* Product Collections */}
          <div id="product-collections" style={{ marginBottom: '60px' }}>
            <Title level={1} style={{ textAlign: 'center', marginBottom: '20px' }}>
              Choose Your Lumi Companion
            </Title>
            <Paragraph style={{ textAlign: 'center', fontSize: '18px', marginBottom: '50px', color: '#7f8c8d' }}>
              Each Lumi companion is designed to provide comfort and support for children who need extra help with big emotions, social challenges, or when parents aren't available.
            </Paragraph>
            
            {collections.map(collection => (
              <div key={collection} style={{ marginBottom: '50px' }}>
                <Title level={2} style={{ textAlign: 'center', marginBottom: '30px', color: '#2c3e50' }}>
                  {collection}
                </Title>
                <Row gutter={[24, 24]}>
                  {Object.values(products)
                    .filter(product => product.collection === collection)
                    .map(product => (
                    <Col xs={24} sm={12} lg={8} key={product.id}>
                      <Card
                        hoverable
                        style={{ 
                          height: '100%',
                          border: selectedProduct === product.id ? '2px solid #e74c3c' : '1px solid #d9d9d9'
                        }}
                        bodyStyle={{ padding: '16px' }}
                        onClick={() => history.push(getProductRoute(product.id))}
                      >
                        <div style={{ 
                          textAlign: 'center', 
                          backgroundColor: '#f5f5f5', 
                          borderRadius: '8px', 
                          padding: '20px',
                          marginBottom: '16px',
                          height: '200px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img 
                            src={product.image} 
                            alt={product.name}
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '100%', 
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = '<div style="font-size: 100px; opacity: 0.8;">🧸</div>';
                            }}
                          />
                        </div>
                        <Title level={5} style={{ marginBottom: '8px', textAlign: 'center' }}>
                          {product.name}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: '12px' }}>
                          {product.subtitle}
                        </Text>
                        <Title level={4} style={{ textAlign: 'center', color: '#e74c3c', marginBottom: '16px' }}>
                          ${product.price}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: '-12px', marginBottom: '16px', fontSize: '13px' }}>
                          Preorder · Guaranteed delivery in summer 2027
                        </Text>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <Button
                            type="default"
                            size="large"
                            onClick={(e) => {
                              e.stopPropagation();
                              trackProductInteraction(product.id, 'learn_more');
                              history.push(getProductRoute(product.id));
                            }}
                            style={{ flex: 1 }}
                          >
                            See How This Helps
                          </Button>
                          <Button
                            type={selectedProduct === product.id ? "primary" : "default"}
                            size="large"
                            icon={<ShoppingCartOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product.id, 1);
                            }}
                            style={{
                              flex: 1,
                              ...(selectedProduct === product.id ? {
                                background: '#e74c3c',
                                borderColor: '#e74c3c'
                              } : {})
                            }}
                          >
                            {selectedProduct === product.id ? "Preorder" : "Select"}
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </div>

          {/* Featured Blog Content Section */}
          <div style={{ marginTop: '80px', marginBottom: '40px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Title level={2} style={{ color: '#2c3e50', marginBottom: '16px' }}>
                Supporting Your Journey
              </Title>
              <Paragraph style={{ fontSize: '18px', color: '#7f8c8d', maxWidth: '600px', margin: '0 auto' }}>
                Expert guidance and insights to help you make the best choices for your child's emotional wellness.
              </Paragraph>
            </div>
            <Row gutter={[24, 24]}>
              {getFeaturedPosts().slice(0, 3).map(post => (
                <Col xs={24} md={8} key={post.id}>
                  <Card
                    hoverable
                    style={{ height: '100%' }}
                    cover={
                      <div style={{ 
                        height: '200px', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '60px'
                      }}>
                        📖
                      </div>
                    }
                    onClick={() => {
                      trackBlogView(post);
                      history.push(`/blog/${post.slug}`);
                    }}
                  >
                    <div style={{ padding: '8px 0' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {post.category} • {post.readTime}
                      </Text>
                      <Title level={5} style={{ marginTop: '8px', marginBottom: '12px', lineHeight: '1.4' }}>
                        {post.title}
                      </Title>
                      <Paragraph 
                        style={{ 
                          color: '#7f8c8d', 
                          fontSize: '14px', 
                          lineHeight: '1.5',
                          marginBottom: '16px' 
                        }}
                        ellipsis={{ rows: 3 }}
                      >
                        {post.excerpt}
                      </Paragraph>
                      <Button 
                        type="link" 
                        style={{ padding: 0, fontWeight: '600', color: '#667eea' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          trackBlogView(post);
                          history.push(`/blog/${post.slug}`);
                        }}
                      >
                        Read Full Article →
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Button 
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  fontWeight: '600',
                  height: '48px',
                  paddingLeft: '32px',
                  paddingRight: '32px',
                  borderRadius: '24px'
                }}
                onClick={() => history.push('/blog')}
              >
                Explore All Articles
              </Button>
            </div>
          </div>

          {/* Newsletter Signup Section */}
          <div style={{ 
            marginTop: '80px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '60px 40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <Title level={2} style={{ color: 'white', marginBottom: '16px' }}>
              Stay Connected with Expert Insights
            </Title>
            <Paragraph style={{ 
              fontSize: '18px', 
              color: 'rgba(255,255,255,0.9)', 
              marginBottom: '32px',
              maxWidth: '500px',
              margin: '0 auto 32px auto'
            }}>
              Get practical tips, research-backed guidance, and early access to new wellness tools for your family.
            </Paragraph>
            <Row justify="center">
              <Col xs={24} md={16} lg={12}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Input
                    size="large"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onPressEnter={handleNewsletterSignup}
                    style={{
                      borderRadius: '25px',
                      border: 'none',
                      padding: '12px 20px',
                      fontSize: '16px'
                    }}
                  />
                  <Button
                    type="primary"
                    size="large"
                    loading={isSubscribing}
                    onClick={handleNewsletterSignup}
                    style={{
                      background: '#e74c3c',
                      borderColor: '#e74c3c',
                      borderRadius: '25px',
                      padding: '12px 30px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Get Updates
                  </Button>
                </div>
                <Text style={{ 
                  fontSize: '14px', 
                  color: 'rgba(255,255,255,0.8)', 
                  marginTop: '12px', 
                  display: 'block' 
                }}>
                  No spam, ever. Unsubscribe anytime.
                </Text>
              </Col>
            </Row>
          </div>

          {/* FAQ Section */}
          <FAQSection 
            faqs={homepageFAQs}
            title="Frequently Asked Questions"
            style={{ marginTop: '80px' }}
          />
        </div>
      </div>

      {/* Shopping Cart Drawer */}
      <Drawer
        title="Your Preorder"
        placement="right"
        onClose={() => setCartVisible(false)}
        open={cartVisible}
        width={400}
        footer={
          cartItems.length > 0 && (
            <div style={{ padding: '20px' }}>
              <Row justify="space-between" style={{ marginBottom: '20px' }}>
                <Text strong style={{ fontSize: '18px' }}>Total: ${getTotalPrice().toLocaleString()}</Text>
              </Row>
              <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
                Preordering guarantees your Lumi in summer 2027.
              </Paragraph>
              <Button
                type="primary"
                size="large"
                block
                style={{
                  background: '#e74c3c',
                  borderColor: '#e74c3c',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  height: '50px'
                }}
                onClick={() => {
                  trackBeginCheckout(cartItems);
                  setCartVisible(false);
                  setCheckoutVisible(true);
                }}
              >
                Complete Preorder
              </Button>
            </div>
          )
        }
      >
        {cartItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '64px', opacity: 0.3, marginBottom: '20px' }}>🛒</div>
            <Text type="secondary">You haven't preordered anything yet</Text>
          </div>
        ) : (
          cartItems.map(item => (
            <Card key={item.id} style={{ marginBottom: '16px' }} bodyStyle={{ padding: '16px' }}>
              <Row align="middle">
                <Col span={8}>
                  <img 
                    src={item.image} 
                    alt={item.name}
                    style={{ width: '100%', maxWidth: '80px', borderRadius: '8px' }}
                  />
                </Col>
                <Col span={10} style={{ paddingLeft: '16px' }}>
                  <div>
                    <Text strong>{item.name}</Text>
                    <br />
                    <Text type="secondary">${item.price}</Text>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                      type="text"
                      icon={<MinusOutlined />}
                      size="small"
                      onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                    />
                    <span style={{ margin: '0 8px', fontWeight: 'bold' }}>{item.quantity}</span>
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          ))
        )}
      </Drawer>

      {/* Checkout Modal */}
      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default Calmi;